import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { auth, googleProvider } from '../lib/firebase.js';
import { onAuthStateChanged, signInWithPopup, signOut, User, signInWithRedirect, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { ensureUserProfile } from '../lib/profile.ts';

type AuthState = {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
};

const Ctx = createContext<AuthState>({
  user: null,
  loading: true,
  signInWithGoogle: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          await ensureUserProfile(u.uid, { displayName: u.displayName || undefined, photoURL: u.photoURL || undefined });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to ensure user profile in Firestore. Check security rules and Firestore enablement.', err);
        }
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    signInWithGoogle: async () => {
      if (!auth || !googleProvider) return;
      try {
        await signInWithPopup(auth, googleProvider);
      } catch (e: any) {
        // Fallback to redirect for popup blockers or Safari ITP
        const code = e?.code || '';
        if (code.includes('popup') && auth && googleProvider) {
          await signInWithRedirect(auth, googleProvider);
        } else {
          throw e;
        }
      }
    },
    signOut: async () => {
      if (!auth) return;
      await signOut(auth);
    },
    signInWithEmail: async (email: string, password: string) => {
      if (!auth) return;
      await signInWithEmailAndPassword(auth, email, password);
    },
    signUpWithEmail: async (email: string, password: string) => {
      if (!auth) return;
      await createUserWithEmailAndPassword(auth, email, password);
    },
  }), [user, loading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}


